import { MouseEvent, useRef } from 'react'
import OverlayScrollbar from '@/shared/components/OverlayScrollbar/OverlayScrollbar'
import { WebsiteDocument } from '../../schema/document'
import BlockRenderer from '../../render/BlockRenderer'
import { RenderContext } from '../../render/context'
import { isoDay } from '../../render/dates'
import { MenuSnapshot } from '../../render/menu'
import { themeClassName, themeStyle } from '../../render/theme'
import { resolveEditorAssetUrl } from '../../utils/assets'
import { useEditorPlaceholders } from '../../hooks/useEditorPlaceholders'
import { useFitScale } from '../../hooks/useFitScale'
import styles from './PagePreview.module.scss'

interface PagePreviewProps {
  document: WebsiteDocument
  language: string
  device: 'desktop' | 'mobile'
  /**
   * Takes the scrollbar away while a dialog covers the page. The bar is
   * `position: absolute` at a z-index of 30 and this app's modals sit at 5 and
   * 6, so one left on screen paints a bright line down the dimmed backdrop.
   */
  hideScrollbar?: boolean
  /**
   * A click on a row of the page, by the row's id. The form beside the
   * preview scrolls to the card that row is made from; without this the page
   * is a picture.
   */
  onSectionClick?: (rowId: string) => void
  /** The venue's menu as it stands, for the menu section; see `render/menu.ts`. */
  menu?: MenuSnapshot | null
}

/** The widths a page is laid out at before it is shrunk to fit the panel. */
const PREVIEW_WIDTH = { desktop: 1200, mobile: 390 }

/**
 * The page as a visitor sees it, with no editor chrome at all.
 *
 * This is what sits beside the simple template form: every keystroke in the
 * form rebuilds the document and this draws it. Same `BlockRenderer` as the
 * published page, in `view` mode, so what the owner is shown here is exactly
 * what they publish.
 */
function PagePreview(props: PagePreviewProps) {
  const { document, language, device, hideScrollbar, onSectionClick, menu } =
    props
  const placeholders = useEditorPlaceholders()
  const hostRef = useRef<HTMLDivElement>(null)
  const width = PREVIEW_WIDTH[device]
  const scale = useFitScale(hostRef, width)
  const page = document.pages[0]

  const context: RenderContext = {
    language,
    defaultLanguage: document.defaultLanguage,
    mode: 'view',
    resolveAssetUrl: resolveEditorAssetUrl,
    resolveHref: () => '#',
    menu,
    // The owner is looking at the page today, so the booking form's date says
    // today. A published page says the day it was built; see the field itself.
    today: isoDay(),
    placeholders
  }

  const handleClick = (event: MouseEvent<HTMLDivElement>) => {
    if (!onSectionClick) return
    const target = event.target as HTMLElement
    // A control on the page keeps its click: a tab, a table, a field, a link.
    if (target.closest('a, button, input, label, select, textarea')) return
    const row = target.closest('section.mp-row')
    if (row?.id) onSectionClick(row.id)
  }

  return (
    <div className={styles.well}>
      {/* Hidden native bar plus the app's overlay one; hidden alone was a
          scroller with no scrollbar. */}
      {!hideScrollbar && <OverlayScrollbar targetRef={hostRef} />}
      {/*
        The frame is the box the page's booking sheet is placed in (see the
        stylesheet): between the well, which keeps the scrollbar's gutter, and
        the scroller, so the sheet covers exactly the width the page is
        centred in and lines up with it.
      */}
      <div className={styles.frame}>
        {/*
          `presentation` on purpose. Clicking a section is a convenience for a
          mouse, not the only way to its card: the form lists every section in
          the same order and is reachable by tab.
        */}
        <div
          className={styles.host}
          ref={hostRef}
          role="presentation"
          onClick={handleClick}
        >
          <div
            className={`${themeClassName(document.theme)} ${styles.page}`}
            // Computed values and geometry, which is what an inline style is for.
            style={{
              ...themeStyle(document.theme),
              width: `${width}px`,
              zoom: scale
            }}
          >
            <BlockRenderer blocks={page.blocks} context={context} />
          </div>
        </div>
      </div>
    </div>
  )
}

export default PagePreview
